import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Box,
  Typography,
  TablePagination,
  Skeleton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  EmojiEvents as AwardIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  School as SchoolIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../../../services/configUrls";
import FacultyRatingDialog from "./FacultyRating";


const FacultyEventsTableSkeleton = () => (
  <TableContainer
    component={Paper}
    sx={{
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      overflow: "hidden",
    }}
  >
    <Table size="small">
      <TableHead>
        <TableRow
          sx={{
            backgroundColor: "#F8FAFC",
            borderBottom: "1px solid #D1D5DB",
          }}
        >
          <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>Name</TableCell>
          <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
            Event Type
          </TableCell>
          <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
            Domain
          </TableCell>
          <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
            Start Date
          </TableCell>
          <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
            End Date
          </TableCell>
          <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
            Trainer Name
          </TableCell>
          <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
            Trainer Email
          </TableCell>
          <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
            Certificate Status
          </TableCell>
          <TableCell>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(5)].map((_, index) => (
          <TableRow key={index}>
            <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
              <Skeleton variant="text" width={120} />
            </TableCell>
            <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
              <Skeleton variant="rounded" width={60} height={24} />
            </TableCell>
            <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
              <Skeleton variant="text" width={200} />
            </TableCell>
            <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
              <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
              <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
              <Skeleton variant="text" width={120} />
            </TableCell>
            <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
              <Skeleton variant="text" width={180} />
            </TableCell>
            <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
              <Skeleton variant="rounded" width={80} height={24} />
            </TableCell>
            <TableCell>
              <Skeleton variant="rounded" width={40} height={32} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const FacultyEventsTable = ({ disablePadding = false, onDataLoad }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loadingDomainId, setLoadingDomainId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [certificateModal, setCertificateModal] = useState({
    open: false,
    pdfUrl: null,
    loading: false,
    bookslotId: null,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchFacultyEvents();
  }, []);

  const fetchFacultyEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/event/faculty-events`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch faculty events");
      }

      const data = await response.json();

      // Combine FDP and EDP events with event type
      const fdpEvents = (data.fdp || []).map((event) => ({
        ...event,
        eventType: "FDP",
      }));
      const edpEvents = (data.edp || []).map((event) => ({
        ...event,
        eventType: "EDP",
      }));

      const combined = [...fdpEvents, ...edpEvents];
      setEvents(combined);
      if (onDataLoad) {
        onDataLoad(combined.length);
      }
    } catch (error) {
      console.error("Error fetching faculty events:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCertificate = async (bookslotId, eventType) => {
    setCertificateModal({
      open: true,
      pdfUrl: null,
      loading: true,
      bookslotId,
    });

    try {
      // Use different endpoint based on event type
      const endpoint =
        eventType === "FDP"
          ? `${BASE_URL}/event/fdp-faculty-certificate/${bookslotId}`
          : `${BASE_URL}/event/edp-faculty-certificate/${bookslotId}`;

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch certificate");
      }

      const data = await response.json();
      const base64Data = data.certificate_base64;

      // Convert base64 to blob and create URL
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      setCertificateModal((prev) => ({
        ...prev,
        pdfUrl: url,
        loading: false,
      }));
    } catch (error) {
      console.error("Error fetching certificate:", error);
      setCertificateModal({
        open: false,
        pdfUrl: null,
        loading: false,
        bookslotId: null,
      });
      alert("Failed to load certificate. Please try again.");
    }
  };

  const handleDownloadCertificate = () => {
    if (certificateModal.pdfUrl) {
      const link = document.createElement("a");
      link.href = certificateModal.pdfUrl;
      link.download = `certificate_${certificateModal.bookslotId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleRatingSubmit = () => {
    fetchFacultyEvents();   // refresh table
  };

  const handleCloseCertificateModal = () => {
    if (certificateModal.pdfUrl) {
      URL.revokeObjectURL(certificateModal.pdfUrl);
    }
    setCertificateModal({
      open: false,
      pdfUrl: null,
      loading: false,
      bookslotId: null,
    });
  };

  const handleMenuOpen = (event, eventData) => {
    setAnchorEl(event.currentTarget);
    setSelectedEvent(eventData);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const handleDownloadCertificateClick = () => {
    if (selectedEvent) {
      fetchCertificate(selectedEvent.bookslot_id, selectedEvent.eventType);
    }
    handleMenuClose();
  };

  const isWithin5DaysOfEndDate = (endDateStr) => {
    if (!endDateStr) return false;
    const parts = endDateStr.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // 0-indexed month
      const day = parseInt(parts[2], 10);
      const endDate = new Date(year, month, day);
      
      const limitDate = new Date(endDate);
      limitDate.setDate(endDate.getDate() + 2);
      limitDate.setHours(23, 59, 59, 999);
      
      const today = new Date();
      return today <= limitDate;
    }
    
    const endDate = new Date(endDateStr);
    const limitDate = new Date(endDate.getTime() + 5 * 24 * 60 * 60 * 1000);
    limitDate.setHours(23, 59, 59, 999);
    return new Date() <= limitDate;
  };

  const handleContinueLearning = async (domainId, bookslotId) => {
    try {
      setLoadingDomainId(domainId);

      const response = await fetch(
        `${BASE_URL}/event/get-process-document?domain_id=${domainId}&bookslot_id=${bookslotId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch process document");
      }

      const data = await response.json();

      if (data?.redirect_link) {
        console.log("redirect", data.redirect_link);
        localStorage.clear();
        window.location.href = data.redirect_link;
        return;
      }

      navigate(`/process/${domainId}/${bookslotId}`, { state: { fromTab: "completed" } });
    } catch (err) {
      console.error("Error checking redirect:", err);
      navigate(`/process/${domainId}/${bookslotId}`, { state: { fromTab: "completed" } });
    } finally {
      setLoadingDomainId(null);
    }
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch =
      event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.domain_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.trainer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.trainer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.eventType.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const paginatedEvents = filteredEvents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDomainColor = (domain) => {
    const colors = [
      "#4CAF50",
      "#2196F3",
      "#FF9800",
      "#9C27B0",
      "#F44336",
      "#00BCD4",
      "#FF5722",
      "#3F51B5",
    ];
    const index =
      domain.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      colors.length;
    return colors[index];
  };

  const getEventTypeColor = (type) => {
    return type === "FDP"
      ? { color: "#2196F3", bgColor: "#E3F2FD" }
      : { color: "#FF9800", bgColor: "#FFF3E0" };
  };

  const getCertificateStatus = (status) => {
    switch (status) {
      case 0:
        return {
          label: "Not Issued",
          color: "#F44336",
          bgColor: "#FFEBEE",
        };
      case 1:
        return {
          label: "Issued",
          color: "#FF9800",
          bgColor: "#FFF3E0",
        };
      case 2:
        return {
          label: "Received",
          color: "#4CAF50",
          bgColor: "#E8F5E9",
        };
      default:
        return {
          label: "Unknown",
          color: "#9E9E9E",
          bgColor: "#F5F5F5",
        };
    }
  };

 const isWithinDate = (date) => {
    if (!date) return false;

    return new Date() <= new Date(date);
};

  const truncateText = (text, maxLength = 30) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <Box sx={{ p: disablePadding ? 0 : 3 }}>
        <Paper
          sx={{
            p: 2,
            mb: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            borderRadius: "12px",
          }}
        >
          <Skeleton
            variant="rounded"
            width={250}
            height={40}
            sx={{ borderRadius: "12px" }}
          />
        </Paper>
        <FacultyEventsTableSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={{ p: disablePadding ? 0 : 3 }}>
      <Paper
        sx={{
          p: 2,
          mb: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          borderRadius: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            placeholder="Search Events..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9CA3AF", fontSize: "1rem" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 250,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
              },
            }}
            size="small"
          />
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          mb: 2,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#F8FAFC",
                borderBottom: "1px solid #D1D5DB",
              }}
            >
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Name
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Event Type
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Domain
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Start Date
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  End Date
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Trainer Name
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Trainer Email
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Certificate Status
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedEvents.map((event, index) => {
              const statusInfo = getCertificateStatus(event.cert_status);
              const eventTypeColor = getEventTypeColor(event.eventType);
              return (
                <TableRow
                  key={event.bookslot_id}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "#F9FAFB" },
                    "& td": {
                      borderBottom:
                        index === paginatedEvents.length - 1
                          ? "none"
                          : "1px solid #E5E7EB",
                    },
                  }}
                >
                  <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      title={event.name}
                    >
                      <PersonIcon
                        sx={{ fontSize: "0.9rem", color: "#6B7280" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "#1F2937", fontWeight: 500 }}
                      >
                        {truncateText(event.name, 25)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                    <Chip
                      label={event.eventType}
                      sx={{
                        backgroundColor: eventTypeColor.bgColor,
                        color: eventTypeColor.color,
                        fontWeight: 600,
                        borderRadius: "8px",
                        fontSize: "0.7rem",
                        height: "24px",
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell
                    sx={{ borderRight: "1px solid #E5E7EB" }}
                    title={event.domain_name}
                  >
                    <Chip
                      label={truncateText(event.domain_name, 30)}
                      sx={{
                        backgroundColor: `${getDomainColor(
                          event.domain_name
                        )}15`,
                        color: getDomainColor(event.domain_name),
                        fontWeight: 500,
                        borderRadius: "8px",
                        fontSize: "0.7rem",
                        height: "24px",
                        maxWidth: "250px",
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: "0.9rem", color: "#6B7280" }}
                      />
                      <Typography variant="body2" sx={{ color: "#4B5563" }}>
                        {formatDate(event.start_date)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: "0.9rem", color: "#6B7280" }}
                      />
                      <Typography variant="body2" sx={{ color: "#4B5563" }}>
                        {formatDate(event.end_date)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ borderRight: "1px solid #E5E7EB" }}
                    title={event.trainer_name}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <PersonIcon
                        sx={{ fontSize: "0.9rem", color: "#6B7280" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "#1F2937", fontWeight: 500 }}
                      >
                        {truncateText(event.trainer_name, 25)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ borderRight: "1px solid #E5E7EB" }}
                    title={event.trainer_email}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <EmailIcon
                        sx={{ fontSize: "0.9rem", color: "#6B7280" }}
                      />
                      <Typography variant="body2" sx={{ color: "#4B5563" }}>
                        {truncateText(event.trainer_email, 30)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                    <Chip
                      label={statusInfo.label}
                      sx={{
                        backgroundColor: statusInfo.bgColor,
                        color: statusInfo.color,
                        fontWeight: 500,
                        borderRadius: "8px",
                        fontSize: "0.7rem",
                        height: "24px",
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, event)}
                      sx={{
                        color: "#6B7280",
                        "&:hover": { backgroundColor: "#F3F4F6" },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {paginatedEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    No events found matching your criteria.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            minWidth: 200,
          },
        }}
      >
        {selectedEvent?.cert_status === 1 || selectedEvent?.cert_status === 2 ? (
          <MenuItem
            onClick={handleDownloadCertificateClick}
            sx={{
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}
          >
            <ListItemIcon>
              <AwardIcon fontSize="small" sx={{ color: "#2196F3" }} />
            </ListItemIcon>
            <ListItemText
              primary="Download Certificate"
              primaryTypographyProps={{ fontSize: "0.875rem" }}
            />
          </MenuItem>
        ) : (
          <MenuItem disabled>
            <ListItemIcon>
              <AwardIcon fontSize="small" sx={{ color: "#9E9E9E" }} />
            </ListItemIcon>
            <ListItemText
              primary="Certificate Not Available"
              primaryTypographyProps={{ fontSize: "0.875rem" }}
            />
          </MenuItem>
        )}
        {/* Course Access and Upload Certificate buttons commented out */}
        {selectedEvent && (
          isWithinDate(selectedEvent?.upload_till) ||
          isWithinDate(selectedEvent?.course_till) ||
          isWithin5DaysOfEndDate(selectedEvent?.end_date)
        ) && (
            <>
              <MenuItem
                onClick={() => {
                  handleContinueLearning(selectedEvent.domain_id, selectedEvent.bookslot_id);
                  handleMenuClose();
                }}
                disabled={loadingDomainId === selectedEvent.domain_id}
                sx={{
                  "&:hover": { backgroundColor: "#F3F4F6" },
                }}
              >
                <ListItemIcon>
                  {loadingDomainId === selectedEvent.domain_id ? (
                    <CircularProgress size={18} sx={{ color: "#4CAF50" }} />
                  ) : (
                    <SchoolIcon fontSize="small" sx={{ color: "#4CAF50" }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Course Access"
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>

              <MenuItem
                onClick={() => {
                  navigate("/upload-certificate", {
                    state: { eventData: selectedEvent, fromTab: "completed" },
                  });
                  handleMenuClose();
                }}
                sx={{
                  "&:hover": { backgroundColor: "#F3F4F6" },
                }}
              >
                <ListItemIcon>
                  <UploadIcon fontSize="small" sx={{ color: "#FF9800" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Upload Certificate"
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
            </>
          )}
      </Menu>

      <Paper
        sx={{ boxShadow: "0 4px 20px rgba(0,0,0,0.08)", borderRadius: "12px" }}
      >
        <TablePagination
          component="div"
          count={filteredEvents.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            {
              color: "#6B7280",
            },
          }}
        />
      </Paper>

      {/* Certificate Modal */}
      <Dialog
        open={certificateModal.open}
        onClose={handleCloseCertificateModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", minHeight: "80vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AwardIcon sx={{ color: "#2196F3" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Certificate Preview
            </Typography>
          </Box>
          <IconButton onClick={handleCloseCertificateModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {certificateModal.loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                py: 8,
              }}
            >
              <CircularProgress size={48} sx={{ color: "#2196F3" }} />
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                Loading certificate...
              </Typography>
            </Box>
          ) : certificateModal.pdfUrl ? (
            <iframe
              src={`${certificateModal.pdfUrl}#toolbar=0`}
              style={{
                width: "100%",
                height: "70vh",
                border: "none",
              }}
              title="Certificate Preview"
            />
          ) : (
            <Typography variant="body2" sx={{ color: "#6B7280", py: 4 }}>
              Failed to load certificate
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #E5E7EB" }}>
          <Button
            onClick={handleCloseCertificateModal}
            sx={{
              color: "#6B7280",
              "&:hover": { backgroundColor: "#F3F4F6" },
              borderRadius: "8px",
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleDownloadCertificate}
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={!certificateModal.pdfUrl || certificateModal.loading}
            sx={{
              backgroundColor: "#4CAF50",
              "&:hover": { backgroundColor: "#45A049" },
              "&:disabled": { backgroundColor: "#CCCCCC" },
              borderRadius: "8px",
            }}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
      <FacultyRatingDialog onSubmitSuccess={handleRatingSubmit} />
    </Box>
  );
};

export default FacultyEventsTable;